import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { Text } from '@atlaskit/primitives/compiled';

import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../common/ui/filters/select-filter';
import { createOptionLabelFormatter } from '../../../../../common/ui/filters/select-filter/option-label';

import { i18n } from './messages';
import { SiteAvatar } from './site-avatar';

const DEFAULT_MAX_SELECTED = 5;

// Renders the parenthetical hint (e.g. "(1 required)") as italic, regular-weight text so it isn't
// bold like the rest of the heading. Passed to the message's `<hint>` chunk.
const renderHint = (chunks: React.ReactNode[]): React.ReactNode => (
	<Text as="em" weight="regular" size="small" color="inherit">
		{chunks}
	</Text>
);

export interface SiteOption {
	/** Unique site identifier. Stored in `selectedSites` and later sent to the search query (QS-10301). */
	cloudId: string;
	/** Site display name, shown as the checkbox label. */
	name: string;
}

export interface SiteSelectProps {
	/** All sites available to choose from. */
	sites: SiteOption[];
	/** Cloud IDs of the currently selected sites. Always contains at least `primarySiteId`. */
	selectedSiteIds: string[];
	/** The current/primary site. Always selected and cannot be deselected.  */
	primarySiteId: string;
	/** Organisation name — fallback title for the available-sites section. Required. */
	orgName: string;
	/** Unit name — title for the available-sites section when provided; falls back to `orgName`. */
	unitName?: string;
	/** Called when an unselected site is checked. */
	onSelectSite: (cloudId: string) => void;
	/** Called when a selected (non-primary) site is unchecked. */
	onUnselectSite: (cloudId: string) => void;
	/** Maximum number of sites that can be selected at once. Defaults to 5. */
	maxSelected?: number;
	/** Opens the dropdown on mount. Primarily for examples/VR tests. */
	defaultIsOpen?: boolean;
	isFilterInDropdown?: boolean;
	isFiltersDiscoverability2Enabled?: boolean;
}

// `SelectFilterOption` doesn't declare `isDisabled`, but the underlying react-select option honours
// it — we widen the option shape so the primary (locked) and cap-limited rows can render disabled.
type SiteSelectOption = SelectFilterOption & { isDisabled?: boolean };

const SiteSelect = ({
	sites,
	selectedSiteIds,
	primarySiteId,
	orgName,
	unitName,
	onSelectSite,
	onUnselectSite,
	maxSelected = DEFAULT_MAX_SELECTED,
	defaultIsOpen,
	isFilterInDropdown,
	isFiltersDiscoverability2Enabled,
}: SiteSelectProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const atCap = selectedSiteIds.length >= maxSelected;
	// The current/primary site is always selected and pinned first, so it is the "first selected value".
	const primarySiteName = sites.find((site) => site.cloudId === primarySiteId)?.name;

	// "Selected" section heading (a React node — the hint is an italic <Text>, no parsing needed):
	//  - only the required current site selected → "Selected (1 required)"
	//  - more selected, and the cap is reachable (>= maxSelected sites exist) → "Selected (Select up to N)"
	//  - otherwise → "Selected"  (the "up to N" hint is dropped when fewer than maxSelected sites exist)
	const selectedSectionHeading: React.ReactNode =
		selectedSiteIds.length <= 1 ? (
			<FormattedMessage {...i18n.selectedHeadingRequired} values={{ hint: renderHint }} />
		) : sites.length >= maxSelected ? (
			<FormattedMessage
				{...i18n.selectedHeadingMax}
				values={{ max: maxSelected, hint: renderHint }}
			/>
		) : (
			<FormattedMessage {...i18n.selectedHeadingPlain} />
		);

	// Available-sites section title: the unit name, falling back to the org name.
	const availableSectionHeading = unitName ?? orgName;

	const buildOption = useCallback(
		(site: SiteOption): SiteSelectOption => {
			const isPrimary = site.cloudId === primarySiteId;
			const isSelected = selectedSiteIds.includes(site.cloudId);
			return {
				value: site.cloudId,
				trackingKey: site.cloudId,
				// State-only, never a URL param — a multi-site param would force non-SSR-friendly
				// multi-site config resolution on the initial load, regressing SSR / VC90.
				queryParamValue: '',
				label: isPrimary ? formatMessage(i18n.currentSiteLabel, { name: site.name }) : site.name,
				isSelected,
				isSuggested: false,
				isDefault: false,
				// Primary can't be deselected; unchecked options lock once the cap is reached.
				isDisabled: isPrimary || (!isSelected && atCap),
			};
		},
		[atCap, formatMessage, primarySiteId, selectedSiteIds],
	);

	const { selectedGroupOptions, availableGroupOptions } = useMemo(() => {
		const selectedSites = sites.filter((site) => selectedSiteIds.includes(site.cloudId));
		// Pin the current/primary site to the top of the "Selected" section.
		selectedSites.sort((a, b) => {
			if (a.cloudId === primarySiteId) {
				return -1;
			}
			if (b.cloudId === primarySiteId) {
				return 1;
			}
			return 0;
		});
		const unselectedSites = sites.filter((site) => !selectedSiteIds.includes(site.cloudId));
		return {
			selectedGroupOptions: selectedSites.map(buildOption),
			availableGroupOptions: unselectedSites.map(buildOption),
		};
	}, [sites, selectedSiteIds, primarySiteId, buildOption]);

	const options = useMemo(
		() =>
			[
				{ label: formatMessage(i18n.selectedHeadingPlain), options: selectedGroupOptions },
				{ label: availableSectionHeading, options: availableGroupOptions },
			].filter((group) => group.options.length > 0),
		[formatMessage, selectedGroupOptions, availableGroupOptions, availableSectionHeading],
	);

	const onSelectChange = useCallback(
		(value: SelectFilterOption | SelectFilterOption[]) => {
			const option = Array.isArray(value) ? value[0] : value;
			if (!option) {
				return;
			}
			const cloudId = option.value;

			// Primary/current site cannot be deselected
			if (cloudId === primarySiteId) {
				return;
			}

			if (selectedSiteIds.includes(cloudId)) {
				onUnselectSite(cloudId);
				return;
			}

			// Respect the selection cap (unchecked rows are also disabled at the cap).
			if (selectedSiteIds.length >= maxSelected) {
				return;
			}
			onSelectSite(cloudId);
		},
		[maxSelected, onSelectSite, onUnselectSite, primarySiteId, selectedSiteIds],
	);

	// Render an avatar before each site's name. The current site's option label carries a
	// "(current)" suffix, so look the name up by cloudId to keep the avatar's initials/colour stable.
	const formatOptionLabel = useMemo(
		() =>
			createOptionLabelFormatter(({ option }) => {
				const site = sites.find((candidate) => candidate.cloudId === option.value);
				return (
					<SiteAvatar
						displayName={site?.name ?? ''}
						isActive={site ? selectedSiteIds.includes(site.cloudId) : false}
					/>
				);
			}),
		[sites, selectedSiteIds],
	);

	return (
		<SelectFilter
			options={options}
			selectedOptions={selectedGroupOptions}
			onSelectChange={onSelectChange}
			defaultIsOpen={defaultIsOpen}
			label={primarySiteName ?? formatMessage(i18n.label)}
			getFirstSelectedLabel={() => ''}
			// Decorative — the chip carries a visible text label (the current site name).
			iconBefore={<OfficeBuildingIcon label="" />}
			formatOptionLabel={formatOptionLabel}
			// The selected group (the one containing the current site) renders the rich heading with the
			// italic hint; the available group renders its plain unit/org name.
			formatGroupLabel={(group) =>
				group.options.some((option) => option.value === primarySiteId)
					? selectedSectionHeading
					: group.label
			}
			checkboxIndicators
			// `selectedOptions` keeps every selected site so the +N badge counts additional sites (e.g.
			// "+1" when one other site is selected), but the chip stays greyed until the user picks a
			// site beyond the always-required current one.
			isSelectedOverride={selectedSiteIds.length > 1}
			// Initial state (only the current site selected) renders with a neutral grey background.
			hasNeutralBackground={selectedSiteIds.length <= 1}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};

export default SiteSelect;
